import { Button, notification, Space } from "@timeedit/ui-components/antd";
import Log, { isLogIgnored, TLogEvent } from "../lib/Log";
import { useEffect } from "react";
import Paragraph from "@timeedit/ui-components/antd/typography/Paragraph";
import { TReferenceItem } from "../types/api/shared";
import Icon, { TEIconType } from "@timeedit/ui-components/components/Icon/Icon";

const AUTO_CLOSE_SECONDS = 5;

const getCopyText = (message: string, details: TReferenceItem[] | undefined) => {
    if (!details || details.length === 0) {
        return message;
    }
    return `${message}\n${details.map((detail) => detail.label).join("\n")}`;
};

const NotificationLogListener = () => {
    const [api, contextHolder] = notification.useNotification();

    const listener = ({ level, context, message }: TLogEvent) => {
        // Escape hatch for certain codes.
        if (isLogIgnored(context)) return;

        let iconId: TEIconType = "Error";
        let isImportant = false;

        if (level < Log.WARNING) {
            isImportant = true;
        }
        if (level === Log.WARNING) {
            iconId = "Warning";
        }
        if (level > Log.WARNING) {
            iconId = "Info";
        }

        const key = `notification-${Date.now()}`;

        const actions = context.actions?.length
            ? context.actions.map((a) => (
                  <Space>
                      <Button type="link" size="small" onClick={a.action}>
                          {a.title}
                      </Button>
                  </Space>
              ))
            : undefined;

        const url = context.url;
        const description = url ? <a href={url}>{message}</a> : <>{message}</>;

        const duration = isImportant ? 0 : AUTO_CLOSE_SECONDS;

        const copyableMessageWithEllipsis = (
            <Paragraph
                ellipsis={{ rows: 6, expandable: false }}
                copyable={
                    context.copyable
                        ? { format: "text/plain", text: getCopyText(message, context.details) }
                        : false
                }
            >
                {description}
            </Paragraph>
        );

        api.open({
            message: copyableMessageWithEllipsis,
            actions,
            key,
            duration,
            icon: <Icon id={iconId} alt={iconId} />,
            style: { maxHeight: 300 },
        });
    };

    // Start listening for certail log levels and display notifications for them.
    useEffect(() => {
        // The non-recoverable levels are deliberately left out - those are presented in a
        // blocking modal by NonRecoverableErrorModal instead.
        return Log.listen(listener, Log.RECOVERABLE_LEVELS);
    }, []);

    // Render context, where the actual Notification component will be rendered.
    return <>{contextHolder}</>;
};

export default NotificationLogListener;
